import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const EMPTY = {
  name: "",
  branch: "",
  contact_person: "",
  phone: "",
  email: "",
  notes: "",
  active: true,
  commission_amount: 0,
};

function FinancierForm({ editing, onSaved, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [editing]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form, commission_amount: Number(form.commission_amount) || 0 };
      if (editing) await api.put(`/financiers/${editing.id}`, payload);
      else await api.post("/financiers", payload);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      data-testid="financier-form"
      className="bg-canvas-card border border-canvas-line p-4 space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <input
          required
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Financier name *"
          data-testid="financier-name"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <input
          value={form.branch}
          onChange={(e) => set("branch", e.target.value)}
          placeholder="Branch"
          data-testid="financier-branch"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <input
          value={form.contact_person}
          onChange={(e) => set("contact_person", e.target.value)}
          placeholder="Contact person"
          data-testid="financier-contact"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <input
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          placeholder="Phone"
          data-testid="financier-phone"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <input
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="Email"
          data-testid="financier-email"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Commission (₹ per finance sale)
          </span>
          <input
            type="number"
            step="0.01"
            value={form.commission_amount}
            onChange={(e) => set("commission_amount", e.target.value)}
            placeholder="0"
            data-testid="financier-commission"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="financier-save"
          className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
        >
          {saving ? "Saving…" : editing ? "Update" : "Add Financier"}
        </button>
      </div>
    </form>
  );
}

export default function FinanciersPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api
      .get("/financiers")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  const remove = async (f) => {
    if (!window.confirm(`Soft-delete ${f.name}?`)) return;
    try {
      await api.delete(`/financiers/${f.id}`);
      toast.success("Deactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const toggle = async (f) => {
    try {
      await api.put(`/financiers/${f.id}`, { ...f, active: !f.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="financiers-page">
      <PageHeader
        eyebrow="05 / Masters"
        title="Financiers"
        subtitle="Banks and NBFCs that finance customer purchases."
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            data-testid="add-financier-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> Add Financier
          </button>
        }
      />
      <div className="px-8 py-6 space-y-4">
        {showForm && (
          <FinancierForm
            editing={editing}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <table className="w-full text-sm" data-testid="financiers-table">
            <thead className="border-b border-canvas-line bg-canvas-panel">
              <tr>
                {["Name", "Branch", "Contact", "Phone", "Email", "Active", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center font-mono text-xs text-ink-subtle"
                  >
                    No financiers yet.
                  </td>
                </tr>
              )}
              {items.map((f) => (
                <tr
                  key={f.id}
                  data-testid={`financier-row-${f.id}`}
                  className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel"
                >
                  <td className="px-4 py-3 font-semibold text-ink">{f.name}</td>
                  <td className="px-4 py-3">{f.branch || "—"}</td>
                  <td className="px-4 py-3">{f.contact_person || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{f.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{f.email || "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggle(f)}
                      data-testid={`financier-toggle-${f.id}`}
                      className="text-ink-muted hover:text-ink"
                      title={f.active ? "Active" : "Inactive"}
                    >
                      {f.active ? (
                        <ToggleRight size={18} weight="bold" className="text-[#00B341]" />
                      ) : (
                        <ToggleLeft size={18} weight="bold" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditing(f);
                          setShowForm(true);
                        }}
                        data-testid={`financier-edit-${f.id}`}
                        className="p-1.5 border border-canvas-line hover:border-ink"
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => remove(f)}
                        data-testid={`financier-delete-${f.id}`}
                        className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
